/**
 * MyGrantsCard — surfaces admin-issued grants (free features/plans) to
 * the user inside /mi-plan, so they understand WHY they have access to
 * something they didn't pay for and know when it expires.
 *
 * Hides itself when the user has no grants — there's no point showing
 * an empty "Regalos" section for paid users.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Gift, CalendarClock, Hash, Sparkles, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { useProfile } from "../../hooks/queries";
import { resolvePriceForUser, formatPrice } from "../../lib/pricingResolver";

const KIND_LABEL_ES = { feature: "Función", plan: "Plan", addon: "Add-on" };
const KIND_LABEL_EN = { feature: "Feature", plan: "Plan", addon: "Add-on" };

// Deep-link map: feature key → page that exercises the feature. We use
// internal app routes; "Open feature" CTA only renders when the bonus
// has a matching entry here, so unknown keys won't show a broken link.
const FEATURE_LINKS = {
  ai_recommendations:  { path: "/recommendations", labelEs: "Abrir Recomendaciones IA", labelEn: "Open AI recommendations" },
  advisor_chat:        { path: "/recommendations", labelEs: "Abrir asesor",             labelEn: "Open advisor" },
  pdf_reports:         { path: "/report",          labelEs: "Generar reporte PDF",      labelEn: "Generate PDF report" },
  weekly_digest:       { path: "/profile",         labelEs: "Configurar digest",        labelEn: "Configure digest" },
  fx_alerts:           { path: "/recommendations", labelEs: "Configurar alerta FX",     labelEn: "Configure FX alert" },
  whatsapp_bot:        { path: "/integrations",    labelEs: "Vincular WhatsApp",        labelEn: "Link WhatsApp" },
  telegram_bot:        { path: "/integrations",    labelEs: "Vincular Telegram",        labelEn: "Link Telegram" },
  whatsapp_record_movements: { path: "/integrations", labelEs: "Probar registro WA",    labelEn: "Try WA logging" },
  telegram_record_movements: { path: "/integrations", labelEs: "Probar registro TG",    labelEn: "Try TG logging" },
  calendar_heatmap:    { path: "/calendar",        labelEs: "Ver heatmap",              labelEn: "View heatmap" },
};

function fmtDate(iso, isEs) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(isEs ? "es-CO" : "en-US", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

export function MyGrantsCard({ isEs }) {
  const { data: profile } = useProfile();
  const userCurrency = String(profile?.currency || "USD").toUpperCase();
  const lang = isEs ? "es" : "en";
  const { data } = useQuery({
    queryKey: ["my-grants"],
    queryFn: async () => (await api.get("/grants/me")).data,
    refetchOnWindowFocus: false,
  });
  const grants = data?.grants || [];
  if (grants.length === 0) return null;  // Hide entirely for non-grant users

  const kindLabel = isEs ? KIND_LABEL_ES : KIND_LABEL_EN;

  return (
    <Card
      className="p-5 rounded-2xl border-2 border-dashed border-emerald-300 bg-gradient-to-br from-emerald-50/60 to-white"
      data-testid="my-grants-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <Gift className="h-4 w-4" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
            {isEs ? "Bonos activos" : "Active bonuses"}
            <Sparkles className="h-3 w-3 text-emerald-600" />
          </h3>
          <p className="text-[11px] text-slate-500">
            {isEs
              ? "Funciones que el equipo de Lumfi te habilitó sin cargo."
              : "Features Lumfi activated on your account at no charge."}
          </p>
        </div>
      </div>

      <ul className="space-y-2" data-testid="my-grants-list">
        {grants.map((g) => {
          const usagePct = g.usage_limit
            ? Math.min(100, Math.round((g.usage_count / g.usage_limit) * 100))
            : null;
          return (
            <li
              key={g.id}
              data-testid={`my-grant-${g.id}`}
              className="p-3 rounded-xl bg-white border border-emerald-100"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">
                      {kindLabel[g.kind] || g.kind}
                    </Badge>
                    <span className="text-sm font-medium text-slate-900">
                      {g.target_label || g.target_id}
                    </span>
                    {/* iter-170s — Show the user-currency price (USD
                        fallback if absent). If no acceptable price
                        exists, hide the decoration silently rather than
                        flash a wrong currency in the user's face. */}
                    {(() => {
                      const pricing = g.target_pricing || {};
                      const resolved = resolvePriceForUser(pricing, userCurrency);
                      if (!resolved) return null;
                      const amt = formatPrice(resolved.entry.amount, resolved.entry.currency, lang);
                      return (
                        <span
                          data-testid={`my-grant-price-${g.id}`}
                          className="text-[11px] text-emerald-700 font-semibold"
                        >
                          · {amt}/{isEs ? "mes" : "mo"}
                        </span>
                      );
                    })()}
                  </div>
                  {/* Feature pills — show what this bonus actually unlocks.
                      For feature grants the label itself is enough so we
                      skip the pill list. For plan/addon grants, this list
                      is the whole answer the user wants to see. */}
                  {g.kind !== "feature" && (g.target_features || []).length > 0 && (
                    <div
                      className="flex flex-wrap gap-1 mt-1.5"
                      data-testid={`my-grant-features-${g.id}`}
                    >
                      {g.target_features.slice(0, 6).map((f) => (
                        <span
                          key={f}
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[9px] font-medium"
                        >
                          ✓ {f}
                        </span>
                      ))}
                      {g.target_features.length > 6 && (
                        <span className="text-[9px] text-slate-500 font-medium">
                          +{g.target_features.length - 6} {isEs ? "más" : "more"}
                        </span>
                      )}
                    </div>
                  )}
                  {g.reason && (
                    <p className="text-[11px] text-slate-500 italic mt-0.5">
                      {g.reason}
                    </p>
                  )}
                </div>
                <div className="text-right text-[11px] flex flex-col gap-0.5">
                  {g.expires_at ? (
                    <span className="text-amber-700 inline-flex items-center gap-1 justify-end">
                      <CalendarClock className="h-3 w-3" />
                      {isEs ? "Hasta " : "Until "}{fmtDate(g.expires_at, isEs)}
                    </span>
                  ) : (
                    <span className="text-emerald-700">
                      ♾️ {isEs ? "Sin expiración" : "No expiry"}
                    </span>
                  )}
                  {g.usage_limit != null && (
                    <span className="text-slate-600 inline-flex items-center gap-1 justify-end">
                      <Hash className="h-3 w-3" />
                      {g.usage_count || 0}/{g.usage_limit} {isEs ? "usados" : "used"}
                    </span>
                  )}
                </div>
              </div>
              {usagePct != null && (
                <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${usagePct}%` }}
                  />
                </div>
              )}
              {/* Deep-link to the feature, when we know where it lives.
                  Only renders for feature/addon bonuses since plan bonuses
                  unlock many features at once — there's no single page
                  to land on. */}
              {(g.kind === "feature" || g.kind === "addon") && FEATURE_LINKS[g.target_id] && (
                <Link
                  to={FEATURE_LINKS[g.target_id].path}
                  data-testid={`my-grant-link-${g.id}`}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-700 hover:text-emerald-800 hover:underline font-medium"
                >
                  <ExternalLink className="h-3 w-3" />
                  {isEs ? FEATURE_LINKS[g.target_id].labelEs : FEATURE_LINKS[g.target_id].labelEn}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
